import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Box, Tab, Typography, CircularProgress } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import MainCard from 'ui-component/cards/MainCard';
import AddUser from '../components/admin/addUser';
import DeleteUser from '../components/admin/deleteUser';
import UpdateStartDate from '../components/admin/updateStartDate';
import AddInternalUser from '../components/admin/addInternalUser';
import DashboardCustomization from '../components/admin/DashboardCustomization';

const ClientOnboard = ({ email, user_role }) => {
  const [value, setValue] = useState('1');
  const [roleLoading, setRoleLoading] = useState(true);
  const [advertiserMetaDetails, setAdvertiserMetaDetails] = useState([]);
  const [advertiserMetaLoading, setAdvertiserMetaLoading] = useState(false);
  const [prefilledEmail, setPrefilledEmail] = useState('');

  const fetchCurrentUserRole = async () => {
    try {
      const response = await fetch('/get_all_users_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          const usersData = JSON.parse(data.data);

          const currentUser = usersData.find((user) => user.email_id === email);
          if (currentUser) {
            const roleMapping = {
              admin: 'admin',
              internal: 'internal',
              client: 'client'
            };

            const mappedRole = roleMapping[currentUser.role] || 'client';

            setRoleLoading(false);

            localStorage.setItem('currentUserRole', mappedRole);

            window.location.reload();
          } else {
            setRoleLoading(false);
          }
        }
      }
    } catch (error) {
      setRoleLoading(false);
    }
  };

  const fetchAdvertiserMetaDetails = async () => {
    try {
      const response = await fetch('/get_advertiser_meta_details');
      const data = await response.json();

      if (response.ok) {
        if (data && data.data && data.data.length > 0) {
          setAdvertiserMetaDetails(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching advertiser meta details:', error);
    }
  };

  const getCurrentRole = () => {
    const cachedRole = localStorage.getItem('currentUserRole');
    if (cachedRole && cachedRole !== 'unknown') {
      return cachedRole;
    }

    const apiRole = localStorage.getItem('user_role');
    if (apiRole && apiRole !== 'unknown') {
      return apiRole;
    }

    return user_role || 'unknown';
  };

  const currentRole = getCurrentRole();

  const isAdmin = currentRole === 'admin';
  const isInternal = currentRole === 'internal';

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleSwitchToDashboardCustomization = (email) => {
    console.log('🔍 Debug - Switching to Dashboard Customization with email:', email);
    setPrefilledEmail(email);
    setValue('2'); // Switch to Dashboard Customization tab
  };

  useEffect(() => {
    const cachedRole = localStorage.getItem('currentUserRole');
    if (cachedRole && cachedRole !== 'unknown') {
      setRoleLoading(false);
      return;
    }

    if (user_role && user_role !== 'unknown') {
      setRoleLoading(false);
    }

    // If we have an email but no role, fetch from get_all_users_list
    if ((!user_role || user_role === 'unknown') && email) {
      fetchCurrentUserRole();
    }
  }, [email, user_role]);

  useEffect(() => {
    fetchAdvertiserMetaDetails();
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const apiRole = localStorage.getItem('user_role');
      if (apiRole && apiRole !== 'unknown') {
        setRoleLoading(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const interval = setInterval(() => {
      const apiRole = localStorage.getItem('user_role');
      if (apiRole && apiRole !== 'unknown') {
        setRoleLoading(false);
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <MainCard title="Configure Dashboard">
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChange} aria-label="Page tabs">
              <Tab label="Onboard Client" value="1" />
              <Tab label="Dashboard Customization" value="2" />
              <Tab label="Delete Client Advertiser" value="3" />
              <Tab label="Update Advertiser Start Date" value="4" />
              {!roleLoading && (isAdmin || isInternal) && <Tab label="InMobi user Access" value="5" />}
              {/* <Tab label="Advertiser Meta Details" value="6" /> */}
              {/* <Tab label="View Client Advertisers" value="7"></Tab> */}
            </TabList>
          </Box>
          <TabPanel value="1" sx={{ width: '100%', p: 0 }}>
            <AddUser initialEmail={email} onSwitchToDashboardCustomization={handleSwitchToDashboardCustomization} />
          </TabPanel>
          <TabPanel value="2" sx={{ width: '100%', p: 0 }}>
            <DashboardCustomization prefilledEmail={prefilledEmail} />
          </TabPanel>
          <TabPanel value="3" sx={{ width: '100%', p: 0 }}>
            <DeleteUser />
          </TabPanel>
          <TabPanel value="4" sx={{ width: '100%', p: 0 }}>
            <UpdateStartDate />
          </TabPanel>
          {!roleLoading && (isAdmin || isInternal) && (
            <TabPanel value="5" sx={{ width: '100%', p: 0 }}>
              <AddInternalUser user_role={user_role} userEmail={email} advertiserMetaDetails={advertiserMetaDetails} />
            </TabPanel>
          )}
          {!roleLoading && !(isAdmin || isInternal) && (
            <TabPanel value="5" sx={{ width: '100%', p: 0 }}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  Access Restricted
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  This tab is only available for Admin and Internal users.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Your current role: <strong>{user_role || 'Not set'}</strong>
                </Typography>
              </Box>
            </TabPanel>
          )}
          {roleLoading && (
            <TabPanel value="5" sx={{ width: '100%', p: 0 }}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Loading user role...
                </Typography>
              </Box>
            </TabPanel>
          )}
          {/* Debug: Show Advertiser Meta Details */}
          {/* {!roleLoading && (isAdmin || isInternal) && (
            <TabPanel value="5" sx={{ width: '100%', p: 0 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Advertiser Meta Details (Debug)
                </Typography>
                {advertiserMetaLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Total Advertisers: {advertiserMetaDetails.length}
                    </Typography>
                    {advertiserMetaDetails.slice(0, 3).map((advertiser, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {advertiser.advertiser_name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>GUID:</strong> {advertiser.advertiser_guid}
                        </Typography>
                        <Typography variant="body2">
                          <strong>SF ID:</strong> {advertiser.salesforce_account_id}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Org:</strong> {advertiser.org_name} ({advertiser.org_id})
                        </Typography>
                        <Typography variant="body2">
                          <strong>Start Date:</strong> {advertiser.start_date}
                        </Typography>
                      </Box>
                    ))}
                    {advertiserMetaDetails.length > 3 && (
                      <Typography variant="body2" color="textSecondary">
                        ... and {advertiserMetaDetails.length - 3} more advertisers
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
                    </TabPanel>
                  )} */}

          {/* <TabPanel value="5"><ViewClientAdvertiserDetails/></TabPanel> */}
        </TabContext>
        {/* <AddUser/> */}
      </MainCard>
    </>
  );
};
const mapStateToProps = (state) => {
  // Try to get user role from localStorage first (more reliable)
  const storedRole = localStorage.getItem('currentUserRole');
  const reduxRole = state.advertiserReducer.user_type;
  const user_role = storedRole || reduxRole || 'unknown';

  console.log('🔍 ClientOnboard - Role determination:', {
    storedRole,
    reduxRole,
    finalRole: user_role,
    advertiserState: state.advertiserReducer
  });

  return {
    experimentList: state.skanRateCardStoreReducer,
    email: state.userReducer.userEmail,
    user_role: user_role
  };
};

const mapDispatchToProps = {
  // No actions needed - we get user role from get_all_users_list
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientOnboard);
